<template>
  <v-container
    class="cart-root fill-height d-flex overflow-hidden pa-0"
    style="max-height: 100%;"
  >
    <success-snackbar
      v-model="snackbar"
      :message="snackbarMessage"
    />
    <error-snackbar
      v-model="cartError"
      :error-message="$t('shop.orderAlreadyCompleted')"
    />
    <div class="fill-height d-flex flex-column flex-grow-1 overflow-y-hidden">
      <div
        v-if="orderId && order && order.orderLines.edges.length > 0 && !order.confirmed"
        class="cart-toolbar"
        data-testid="cart-toolbar"
      >
        <ContinueShopping :handle-click="closeCart" />
        <empty-cart-button
          :order-id="orderId ?? ''"
          :is-disabled="!orderId || initialLoading || order?.orderLines?.edges?.length == 0"
          @onRemoved="onCartCleared"
        />
      </div>
      <apollo-error-alert
        v-if="fetchError"
        :error="fetchError"
      />
      <!-- Skeleton only on very first load with no cached data -->
      <div
        v-else-if="initialLoading"
        class="cart-loading-list flex-grow-1"
      >
        <div
          v-for="n in loadingItemsCount"
          :key="n"
          class="cart-loading-item"
        >
          <v-skeleton-loader
            type="image"
            width="56px"
            height="80px"
            class="cart-loading-thumb"
          />
          <div class="cart-loading-text">
            <v-skeleton-loader
              type="heading"
              width="100%"
            />
            <v-skeleton-loader
              type="text"
              width="60%"
            />
          </div>
        </div>
      </div>
      <div
        v-else-if="orderId && order && order.orderLines.edges.length > 0 && !order.confirmed"
        class="flex-grow-1 d-flex flex-column overflow-y-hidden"
      >
        <div class="cart-items-section flex-grow-1 overflow-auto">
          <h2 class="cart-heading">
            {{ $t("checkout.yourOrder") }}
          </h2>
          <div class="cart-items-list">
            <div
              v-for="(orderLine, index) in order.orderLines.edges.map(
                (edge) => edge.node
              )"
              :key="orderLine.id"
              class="cart-item-wrapper"
              :class="{ 'cart-item-wrapper--animate': shouldAnimate }"
              :style="shouldAnimate ? { animationDelay: itemDelay(index) + 'ms' } : undefined"
            >
              <cart-list-item
                :order="order"
                :order-line="orderLine"
                :show-quantity-input="true"
                @onRemoved="onItemRemoved"
                @quantityInput="optimisticQuantityUpdate"
                @quantityFailed="rollbackOptimisticUpdate"
              />
            </div>
          </div>
        </div>
        <OrderTotals
          :order="order"
          :set-snackbar-message="setSnackbarMessage"
          @cartUpdated="fetchCart(true)"
        />
        <div class="cart-actions">
          <PrimaryButton
            v-if="!$auth.isAuthenticated"
            class="cart-cta-btn"
            block
            :height="buttonHeight"
            @click="login"
          >
            {{ $t("checkout.loginAndCheckout") }}
          </PrimaryButton>
          <NoCostOrder
            v-else-if="order?.totalPrice == 0"
            class="d-flex flex-grow-1 fill-width"
            container-classes="d-none"
            :height="buttonHeight"
            block
          />
          <PrimaryButton
            v-else
            class="cart-cta-btn"
            block
            :height="buttonHeight"
            @click="checkout"
          >
            {{ $t("checkout.checkout") }}
          </PrimaryButton>
        </div>
      </div>
      <EmptyCartState
        v-else-if="!initialLoading"
        variant="drawer"
        @browse="closeCart"
      />
    </div>
  </v-container>
</template>

<script>
import SuccessSnackbar from "@/components/SuccessSnackbar";
import currencyMixins from "@/mixins/currencyMixins";
import CartListItem from "@/views/checkout/Cart/CartListItem";
import ApolloErrorAlert from "@/components/ApolloErrorAlert";
import ErrorSnackbar from "@/components/ErrorSnackbar";
import CouponMutations from "@/graphql/CouponMutations";
import EmptyCartButton from "./EmptyCartButton";
import gql from "graphql-tag";
import OrderFragments from "@/graphql/OrderFragments";
import OrderTotals from "@/views/checkout/Cart/OrderTotals";
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import ContinueShopping from "@/components/ContinueShopping.vue";
import EmptyCartState from "@/components/EmptyCartState.vue";
import NoCostOrder from "@/views/checkout/NoCostOrder.vue";
import { gtagOrderEvent } from "@/helpers/gtagHelper";
import optimisticCartUpdateMixin from "@/mixins/optimisticCartUpdateMixin";

var images = require.context("@/assets/img/", false, /\.jpeg$/);

const ORDER_DETAILS_FRAGMENT = OrderFragments.orderDetailsFragment;

export default {
  name: "CartScreen",
  metaInfo() {
    return {
      title: this.$t('routes.cart'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.$t('seo.cartDescription'),
        },
      ],
    }
  },
  components: {
    CartListItem,
    SuccessSnackbar,
    ApolloErrorAlert,
    ErrorSnackbar,
    EmptyCartState,
    EmptyCartButton,
    OrderTotals,
    PrimaryButton,
    ContinueShopping,
    NoCostOrder,
  },
  mixins: [currencyMixins, optimisticCartUpdateMixin],
  props: {
    closeCart: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      snackbar: false,
      snackbarMessage: "",
      loadingItemsCount: 6,
      cartError: false,
      fetchError: null,
      // Start fetching=true when we have an orderId but no cached data, so the
      // very first render shows the skeleton instead of the empty-cart state.
      fetching: Boolean(this.$store.state.cart.currentCart && !this.$store.state.cart.cartData),
      shouldAnimate: true,
    };
  },
  computed: {
    getOrderQuery() {
      return gql`
        query GetOrder($orderId: ID!) {
          order(id: $orderId) {
            ...OrderDetails
          }
        }
        ${ORDER_DETAILS_FRAGMENT}
      `;
    },
    orderId() {
      return this.$store.state.cart.currentCart;
    },
    order() {
      return this.$store.state.cart.cartData;
    },
    initialLoading() {
      return this.fetching && !this.order;
    },
    buttonHeight() {
      return "44px";
    },
  },
  watch: {
    orderId(newId) {
      if (newId) {
        this.fetchCart();
      } else {
        this.$store.commit("setCartData", null);
      }
    },
    order(newOrder) {
      // When order data loads async, animated items didn't exist at mount.
      // Listen for their entrance animation to finish before disabling.
      if (newOrder && this.shouldAnimate) {
        this.disableAnimationAfterEntrance();
      }
    },
  },
  mounted() {
    if (this.orderId) {
      const hasValidCachedData = this.order && this.order.orderLines.edges.length > 0 && !this.order.confirmed;
      const isStale = this.$store.state.cart.cartDataStale;

      if (hasValidCachedData && !isStale) {
        this.sendViewCartEvent(this.order);
      } else {
        // Force network fetch; stale data stays visible while we refetch
        this.fetchCart(true);
      }
    }
    this.disableAnimationAfterEntrance();
  },
  methods: {
    imgUrl: function (path) {
      return images("./" + path);
    },
    /** Stagger delay per item, capped so large carts don't wait too long. */
    itemDelay(index) {
      const MAX_STAGGER_ITEMS = 8;
      const DELAY_PER_ITEM_MS = 50;
      return Math.min(index, MAX_STAGGER_ITEMS) * DELAY_PER_ITEM_MS;
    },
    /**
     * Wait for the last animated cart item to finish its entrance animation,
     * then disable the animation flag so data updates don't replay it.
     * Falls back to a timeout if no animated items are rendered yet.
     */
    disableAnimationAfterEntrance() {
      if (!this.shouldAnimate) return;
      this.$nextTick(() => {
        const items = this.$el.querySelectorAll('.cart-item-wrapper--animate');
        const lastItem = items[items.length - 1];
        if (lastItem) {
          lastItem.addEventListener('animationend', () => {
            this.shouldAnimate = false;
          }, { once: true });
        }
      });
    },
    async fetchCart(forceNetwork = false) {
      if (!this.orderId) return;
      this.fetching = true;
      this.fetchError = null;
      try {
        const result = await this.$apollo.query({
          query: this.getOrderQuery,
          variables: { orderId: this.orderId },
          fetchPolicy: forceNetwork ? "network-only" : "cache-first",
        });
        const order = result.data?.order;
        if (!order) {
          this.$store.commit("setCurrentCart", null);
        } else {
          this.$store.commit("setCartData", order);
          this.handleCouponCleanup(order);
          this.sendViewCartEvent(order);
        }
      } catch (e) {
        // Only show error if we have no cached data to fall back on
        if (!this.order) this.fetchError = e;
      } finally {
        this.fetching = false;
      }
    },
    handleCouponCleanup(order) {
      if (order?.orderLines?.edges?.length == 0 && order?.orderCoupon) {
        this.$apollo.mutate({
          mutation: CouponMutations.removeCoupon,
          variables: { id: order.id },
          update: () => this.fetchCart(true),
        });
      }
    },
    async login() {
      await this.$auth.loginWithRedirect({
        appState: { targetUrl: "/checkout" },
      });
    },
    checkout() {
      if (this.order?.confirmed) {
        this.cartError = true;
        return;
      }
      gtagOrderEvent('begin_checkout', this.order);
      this.closeCart();
      this.$router.push({ name: "checkout" });
    },
    async onCartCleared() {
      await this.fetchCart(true);
      this.setSnackbarMessage(this.$t("checkout.cartCleared"));
    },
    setSnackbarMessage(value) {
      this.snackbarMessage = value;
      this.snackbar = true;
    },
    rollbackOptimisticUpdate() {
      this.fetchCart(true);
    },
    async onItemRemoved() {
      await this.fetchCart(true);
      this.setSnackbarMessage(this.$t("checkout.itemRemoved"));
    },
    sendViewCartEvent(order) {
      try {
        if (order && order.orderLines.edges.length > 0 && !order.confirmed) {
          gtagOrderEvent('view_cart', order);
        }
      } catch (error) {
        console.error('Failed to send view_cart event to Google Analytics:', error);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.cart-root {
  background: white;
  font-family: $book-font-body;
}

.cart-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid $book-grey-lighter;
}

/* Loading skeleton */
.cart-loading-list {
  padding: 20px 16px;
  overflow-y: auto;
}

.cart-loading-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid $book-grey-lighter;
}

.cart-loading-thumb {
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
}

.cart-loading-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Items section */
.cart-items-section {
  padding: 0 16px;
}

.cart-heading {
  font-family: $book-font-body;
  font-style: italic;
  font-weight: 500;
  font-size: 1.25rem;
  color: $book-ink;
  padding: 20px 0 12px;
  margin: 0;
  letter-spacing: -0.01em;
}

/* Stagger animation */
.cart-items-list {
  display: flex;
  flex-direction: column;
}

.cart-item-wrapper--animate {
  animation: cartItemSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.cart-item-wrapper + .cart-item-wrapper {
  border-top: 1px solid $book-grey-lighter;
}

@keyframes cartItemSlideIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* CTA buttons */
.cart-actions {
  padding: 12px 16px;
  border-top: 1px solid $book-grey-lighter;
}

.cart-cta-btn,
::v-deep .cart-actions .primary-button {
  font-family: $book-font-body !important;
  font-weight: 700 !important;
  font-size: 0.875rem !important;
  letter-spacing: 0.01em !important;
  text-transform: none !important;
  border-radius: 10px !important;
  transition: box-shadow 0.3s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.3s cubic-bezier(0.22, 1, 0.36, 1) !important;

  &:hover {
    box-shadow: 0 3px 16px rgba($book-red, 0.32) !important;
    transform: translateY(-1px);
  }
}

::v-deep .cart-cta-btn.v-btn,
::v-deep .cart-actions .primary-button.v-btn {
  background-image: $book-brand-gradient !important;
}

</style>
